import AppConstants from "../constants/AppConstants";
import { ensureArray } from "./JSUtils";
import { getSubParent, propIsSub, shouldPropInherit } from "./Merge/MergeUtils.mjs";
import { getTypeStylePropName, isSyncMixed } from "./InheritanceUtils";

import {
  LAYOUTS,
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  MIXED,
  SYNCED_PROPS,
} from "../constants/DesignConstants.mjs";
import { DEVIATE, MERGE, SPLIT, STYLE } from "../constants/PropUpdateConstants.mjs";

const { UPDATE_ACTION_DELETE } = AppConstants;

/**
 * @typedef {import("immutable").Map} ImmutableMap
 * @typedef {import("./MulticastUtils").PropUpdate} PropUpdate
 * @typedef {typeof LAYOUTS[number]} Layout
 * @typedef {[propName: string, updateLayout: LAYOUTS[number] | ""]} UpdateDetails
 */

/**
 * Gets propName and layout of an update.
 * @param {PropUpdate} propInfo
 * @return {UpdateDetails}
 */
export const getPropUpdateDetails = ({ name = "" }) => {
  const updateLayout = name.slice(0, name.indexOf("."));
  const validLayout = LAYOUTS.includes(updateLayout);
  const propName = validLayout ? name.slice(name.indexOf(".") + 1) : name;
  return [propName, validLayout ? updateLayout : ""];
};

/**
 * Changes the layout the update is pointed to.
 * @param {PropUpdate} propInfo
 * @param {Layout} newLayout
 * @returns {PropUpdate}
 */
export const redirectUpdate = (propInfo, newLayout) => {
  const [propName] = getPropUpdateDetails(propInfo);
  return { ...propInfo, name: `${newLayout ? `${newLayout}.` : ""}${propName}` };
};

/**
 * Delete update helper. Mostly for showing intent in code rather than an object literal.
 * @param {String} name
 * @param {Boolean} addUndo
 * @returns {PropUpdate}
 */
export const deleteProp = (name, addUndo = false) => ({
  name,
  action: UPDATE_ACTION_DELETE,
  value: null,
  addUndo,
});

/** UPDATE UPDATE SET TYPE DETECTION */

/**
 * Checks propInfo to see if it is a style update.
 * @param {ImmutableMap} elementData
 * @param {PropUpdate | Array<PropUpdate>} propInfo
 * @returns {Boolean}
 */
export const isStyleApplication = (elementData, propInfo) => {
  const typeProp = getTypeStylePropName(elementData);

  return !!ensureArray(propInfo).find((singlePropInfo) => {
    const [propName] = getPropUpdateDetails(singlePropInfo);
    return propName === typeProp;
  });
};

/**
 * Checks to see if an update should be considered a deviation. Will return false if a parent prop is already
 * deviated, since pulling the parent would possibly override local props.
 * @param {ImmutableMap} element
 * @param {PropUpdate | Array<PropUpdate>} propInfo
 * @param {ImmutableMap} theme
 * @returns {Boolean}
 */
export const updateIsDeviation = (element, propInfo, theme) => {
  const updates = ensureArray(propInfo);
  const baseUpdate = updates[0];
  const [propName, updateLayout] = getPropUpdateDetails(baseUpdate);
  const shouldInherit = shouldPropInherit(element, updateLayout, propName, theme);

  const isPropSub = propIsSub(element, propName);
  const parentProp = getSubParent(element, propName);

  const inherit =
    isPropSub && shouldInherit
      // don't deviate if parent prop is already deviated
      ? shouldPropInherit(element, updateLayout, parentProp, theme)
      : shouldInherit;

  return !updateIsDeletion(baseUpdate) && inherit;
};

/**
 * Detects if single prop represents a deletion.
 * @param {PropUpdate} propInfo
 * @returns {Boolean}
 */
export const updateIsDeletion = (propInfo) =>
  [undefined, null].includes(propInfo.value) || propInfo.action === UPDATE_ACTION_DELETE;

/**
 * Detects if a set of updates was produced from a split.
 * @param {PropUpdate | Array<PropUpdate>} propInfo
 * @returns {Boolean}
 */
export const updateIsSplit = (propInfo) => {
  const updates = ensureArray(propInfo);
  const syncUpdate = updates.find(({ name }) => name === SYNCED_PROPS);
  const mobileUpdate = updates.find(
    ({ name, value }) => name?.includes(LAYOUT_MOBILE) && ![undefined, null].includes(value)
  );
  const desktopUpdate = updates.find(
    ({ name, value }) => name?.includes(LAYOUT_DESKTOP) && ![undefined, null].includes(value)
  );

  if (mobileUpdate && syncUpdate) {
    const [propName] = getPropUpdateDetails(mobileUpdate);
    return !syncUpdate.value?.includes(propName);
  }

  // detect split from single element where values haven't been deviated yet
  if (desktopUpdate && mobileUpdate && updates.length === 2) {
    const [desktopProp] = getPropUpdateDetails(mobileUpdate);
    const [mobileProp] = getPropUpdateDetails(desktopUpdate);
    return desktopProp === mobileProp;
  }

  return false;
};

/**
 * Detects if a set of updates was produced from a sync.
 * @param {PropUpdate | Array<PropUpdate>} propInfo
 * @returns {Boolean}
 */
export const updateIsMerge = (propInfo) => {
  const updates = ensureArray(propInfo);
  const syncUpdate = updates.find(({ name }) => name === SYNCED_PROPS);
  const mobileUpdate = updates.find(
    ({ name, value }) => name?.includes(LAYOUT_MOBILE) && [undefined, null].includes(value)
  );
  const desktopUpdate = updates.find(
    ({ name, value }) => name?.includes(LAYOUT_DESKTOP) && ![undefined, null].includes(value)
  );

  if (syncUpdate && mobileUpdate && desktopUpdate && updates.length === 3) {
    const [mobilePropName] = getPropUpdateDetails(mobileUpdate);
    const [desktopPropName] = getPropUpdateDetails(desktopUpdate);
    return desktopPropName === mobilePropName && syncUpdate?.value?.includes(mobilePropName);
  }

  return false;
};

export const updateShouldForceMerge = (propInfo, preparedElementData) => {
  if (!preparedElementData) return false;

  const updates = ensureArray(propInfo);
  const baseUpdate = updates[0];
  const [propName] = getPropUpdateDetails(baseUpdate);
  return isSyncMixed(propName, preparedElementData);
};

/**
 * Gers update type based on update set.
 * @param {Object} props
 * @param {ImmutableMap} props.elementData
 * @param {ImmutableMap=} props.preparedElementData
 * @param {PropUpdate | Array<PropUpdate>} props.propInfo
 * @param {ImmutableMap} props.theme
 * @returns {typeof import("../constants/PropUpdateConstants.mjs").PROCESSES[number] | undefined}
 */
export const getUpdateType = ({ elementData, preparedElementData, propInfo, theme }) =>
  isStyleApplication(elementData, propInfo)
    ? STYLE
    : updateIsMerge(propInfo)
    ? MERGE
    : updateIsSplit(propInfo)
    ? SPLIT
    : updateIsDeviation(elementData, propInfo, theme)
    ? DEVIATE
    : undefined;

/** SINGLE PROP TYPE DETECTION */

/**
 * Returns true if update appears to be an alpha change.
 * @param {PropUpdate} propInfo
 * @returns {Boolean}
 */
export const updateIsAlphaChange = ({ value, origValue }) =>
  (typeof value === "string" && value.startsWith(`${MIXED}:`)) ||
  (typeof origValue === "string" && origValue.startsWith(`${MIXED}:`) && value === MIXED);
